/* assets/luba-events.js — Luba Tasting Events
 * Filter by state and free-text, group by month, render compact tiles.
 * No map, no third-party dependency.
 */
(function () {
  'use strict';

  var MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
  var DOWS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  function parseDate(iso) {
    var p = String(iso).split('-');
    return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function readJSON(root, sel, fallback) {
    var node = root.querySelector(sel);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); }
    catch (e) { console.warn('[luba-events] bad JSON in ' + sel, e); return fallback; }
  }

  /* ── Longboard Connect ──────────────────────────────────────────── */

  function lbUrl(cfg, path, params) {
    var u = (cfg.feedBase || 'https://app.longboard.surf').replace(/\/+$/, '') + path;
    var q = ['shop=' + encodeURIComponent(cfg.shopDomain || '')];
    Object.keys(params || {}).forEach(function (k) {
      if (params[k] !== null && params[k] !== undefined && params[k] !== '') {
        q.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
      }
    });
    return u + '?' + q.join('&');
  }

  function lbJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
      return r.json();
    });
  }

  function normPart(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Same redundant-locality strip as the locator — Connect's addressLine1
  // usually restates city/state/zip, which would print twice on the tile.
  function streetOnly(line1, city, state, zip) {
    var s = String(line1 || '').trim();
    if (!s) return s;
    var parts = s.split(',');
    if (parts.length < 2) return s;
    var nCity = normPart(city), nState = normPart(state), nZip = normPart(zip);
    var redundant = [nCity, nState, nZip, nState + nZip, nCity + nState, nCity + nState + nZip]
                      .filter(Boolean);
    while (parts.length > 1) {
      var last = normPart(parts[parts.length - 1]);
      if (last && redundant.indexOf(last) === -1) break;
      parts.pop();
    }
    return parts.join(',').replace(/[, ]+$/, '').trim();
  }

  // "16:00" -> "4pm", "16:30" -> "4:30pm"
  function clockLabel(hhmm) {
    var p = String(hhmm || '').split(':');
    var h = Number(p[0]), m = Number(p[1]);
    if (isNaN(h)) return '';
    var suffix = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + (m ? ':' + (m < 10 ? '0' + m : m) : '') + suffix;
  }

  // ⚠ Slice the ISO string; do NOT `new Date()` it. startAt carries the VENUE's
  // UTC offset ("2026-09-21T16:00:00-05:00"), so parsing it converts to the
  // viewer's timezone and a Milwaukee 4pm tasting reads as 2pm to someone in
  // Denver. Slicing keeps both the date and the clock venue-local, which is the
  // only correct reading for "when should I turn up".
  /* ── .ics generation ─────────────────────────────────────────────── */

  var BS = String.fromCharCode(92);   // backslash, built not typed
  var CRLF = String.fromCharCode(13, 10);

  // RFC 5545 text escaping. Built with split/join and fromCharCode rather than
  // regex literals so no escape sequence can be mangled in transit and silently
  // corrupt every calendar file.
  function icsEscape(v) {
    var s = String(v == null ? '' : v);
    s = s.split(BS).join(BS + BS);
    s = s.split(';').join(BS + ';');
    s = s.split(',').join(BS + ',');
    s = s.split(String.fromCharCode(13)).join('');
    s = s.split(String.fromCharCode(10)).join(BS + 'n');
    return s;
  }

  // "2026-09-21T16:00:00-05:00" -> "20260921T210000Z"
  //
  // ⚠ Parsing with `new Date()` is CORRECT here, and is deliberately the
  // opposite of the rule used for the display date. There we slice the string,
  // because we want the venue's LOCAL day and parsing would shift it. Here we
  // want the absolute INSTANT, so the offset must be honoured — the calendar
  // entry then lands at the right real-world time in any viewer's zone.
  function icsStamp(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  function buildIcs(e, fullAddress) {
    var start = icsStamp(e.startAt);
    // No end time in the feed: assume two hours rather than emit a zero-length
    // event, which some clients drop entirely.
    var end = icsStamp(e.endAt) || icsStamp(new Date(new Date(e.startAt).getTime() + 7200000).toISOString());
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Luba Libations//Tasting Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + icsEscape((e.id || start) + '@drinkluba.com'),
      'DTSTAMP:' + icsStamp(new Date().toISOString()),
      'DTSTART:' + start,
      'DTEND:' + end,
      'SUMMARY:' + icsEscape('Luba tasting - ' + e.venue),
      'LOCATION:' + icsEscape(fullAddress),
      'DESCRIPTION:' + icsEscape('Come taste Luba at ' + e.venue +
        (e.time ? ' (' + e.time + ')' : '') + '. See all tastings at drinkluba.com/pages/events'),
      'END:VEVENT',
      'END:VCALENDAR'
    ];
    return lines.join(CRLF) + CRLF;
  }

  function icsFilename(e) {
    var slug = String(e.venue || 'tasting').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return ('luba-tasting-' + (slug || 'event') + '-' + (e.date || '')).replace(/-+$/, '') + '.ics';
  }

  // The blob is built on first interaction, not at render time: the schedule
  // draws up to ~250 tiles and pre-creating an object URL for every one would
  // hold them all for the life of the page. mousedown/touchstart/focus all fire
  // before the click completes, so the href is real by the time it is followed.
  function attachIcs(a, e, fullAddress) {
    a.setAttribute('download', icsFilename(e));
    a.href = 'about:blank';
    var made = false;
    function make() {
      if (made) return;
      made = true;
      try {
        a.href = URL.createObjectURL(new Blob([buildIcs(e, fullAddress)], { type: 'text/calendar;charset=utf-8' }));
      } catch (err) {
        made = false;
        console.warn('[luba-events] could not build calendar file', err);
      }
    }
    a.addEventListener('mousedown', make);
    a.addEventListener('touchstart', make, { passive: true });
    a.addEventListener('focus', make);
    a.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') make();
    });
  }

  function adaptEvent(r) {
    var startClock = String(r.startAt || '').slice(11, 16);
    var endClock   = String(r.endAt || '').slice(11, 16);
    var range = clockLabel(startClock);
    // \u2013, not a literal en dash: theme assets are served without a charset,
    // so a raw multi-byte character in a string literal decodes as Latin-1 and
    // renders it as mojibake. Keep every string literal in this file pure ASCII.
    if (endClock) range += '\u2013' + clockLabel(endClock);
    if (r.timeZoneAbbreviation) range += ' ' + r.timeZoneAbbreviation;

    return {
      id: r.id,
      venue: r.venueName || r.title || '',
      retailer: r.retailer || '',
      address: streetOnly(r.addressLine1, r.city, r.state, r.postalCode),
      city: r.city || '',
      state: r.state || '',
      zip: r.postalCode || '',
      date: String(r.startAt || '').slice(0, 10),
      time: range,
      // The RAW offset-bearing timestamps, kept for the .ics. The display
      // fields above are sliced strings and cannot be converted back.
      startAt: r.startAt || '',
      endAt: r.endAt || '',
      // Connect's public event record carries no event-type field yet, so every
      // pill currently renders the design's documented fallback. The day the
      // feed emits `kind` (Tasting | Festival | Pop-up | Market) the colour
      // coding lights up with no theme change.
      kind: r.kind || 'Tasting',
      lat: r.latitude,
      lng: r.longitude
    };
  }

  function Events(root) {
    this.root = root;
    this.cfg = readJSON(root, '[data-events-config]', {});
    this.all = readJSON(root, '[data-events-list]', []) || [];

    if (this.cfg.hidePast) {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      this.all = this.all.filter(function (e) { return parseDate(e.date) >= today; });
    }

    this.el = {
      input:  root.querySelector('[data-events-input]'),
      chips:  root.querySelector('[data-events-chips]'),
      count:  root.querySelector('[data-events-count]'),
      groups: root.querySelector('[data-events-groups]')
    };

    this.state = { q: '', state: 'All' };

    var self = this;
    this.el.input.addEventListener('input', function () {
      self.state.q = self.el.input.value;
      self.render();
    });

    // Pasted test data wins when present; otherwise the live Longboard feed.
    if (this.all.length) {
      this.renderChips();
      this.render();
    } else {
      this.loadFromLongboard();
    }
  }

  // Longboard Connect serves the schedule live, with a server-enforced 90-day
  // forward window and visibility evaluated per request.
  Events.prototype.loadFromLongboard = function () {
    var self = this;
    this.el.groups.innerHTML = '';
    this.el.groups.appendChild(el('div', 'le-loading', 'Loading tastings\u2026'));

    lbJson(lbUrl(this.cfg, '/api/public/shopify/events'))
      .then(function (data) {
        var list = (Array.isArray(data) ? data : (data.events || [])).map(adaptEvent);
        if (self.cfg.hidePast) {
          var today = new Date();
          today.setHours(0, 0, 0, 0);
          list = list.filter(function (e) { return parseDate(e.date) >= today; });
        }
        self.all = list;
        self.renderChips();
        self.render();
      })
      .catch(function (err) {
        console.warn('[luba-events] event feed failed', err);
        self.el.groups.innerHTML = '';
        var wrap = el('div', 'le-empty');
        wrap.appendChild(el('div', 'le-empty-h', 'Schedule unavailable'));
        wrap.appendChild(el('p', 'le-empty-p',
          'We could not load the tasting schedule just now. Try a refresh.'));
        self.el.groups.appendChild(wrap);
      });
  };

  Events.prototype.renderChips = function () {
    var self = this;
    var states = ['All'];
    this.all.forEach(function (e) {
      if (states.indexOf(e.state) === -1) states.push(e.state);
    });
    this.el.chips.innerHTML = '';
    states.forEach(function (s) {
      var b = el('button', 'le-chip', s === 'All' ? 'All states' : s);
      b.type = 'button';
      b.dataset.state = s;
      b.addEventListener('click', function () {
        self.state.state = s;
        self.render();
      });
      self.el.chips.appendChild(b);
    });
  };

  Events.prototype.results = function () {
    var q = this.state.q.trim().toLowerCase();
    var st = this.state.state;
    return this.all
      .filter(function (e) {
        if (st !== 'All' && e.state !== st) return false;
        if (!q) return true;
        return [e.venue, e.city, e.zip, e.address, e.state]
          .join(' ').toLowerCase().indexOf(q) !== -1;
      })
      .sort(function (a, b) { return String(a.date).localeCompare(String(b.date)); });
  };

  Events.prototype.render = function () {
    var list = this.results();
    var self = this;

    Array.prototype.forEach.call(this.el.chips.children, function (b) {
      var on = b.dataset.state === self.state.state;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var where = this.state.state !== 'All' ? this.state.state : 'all locations';
    this.el.count.textContent =
      (list.length === 1 ? '1 tasting' : list.length + ' tastings') + ' coming up in ' + where;

    this.el.groups.innerHTML = '';

    if (!list.length) {
      this.el.groups.appendChild(this.emptyState());
      return;
    }

    var groups = [];
    var index = {};
    list.forEach(function (e) {
      var d = parseDate(e.date);
      var key = d.getFullYear() + '-' + d.getMonth();
      if (!(key in index)) {
        index[key] = groups.length;
        groups.push({ label: MONTHS[d.getMonth()] + ' ' + d.getFullYear(), events: [] });
      }
      groups[index[key]].events.push(e);
    });

    groups.forEach(function (g) {
      var wrap = el('div', 'le-group');

      var head = el('div', 'le-grouphead');
      head.appendChild(el('h2', 'le-h2', g.label));
      head.appendChild(el('span', 'le-rule'));
      // DISTINCT days, not the number of tastings. Several tastings routinely
      // land on the same day — September is 24 tastings across 10 days — so
      // counting events here printed "24 dates" for 10 actual dates. The label
      // says dates; it now counts dates, and it tells the shopper something the
      // tile count does not.
      var days = {};
      g.events.forEach(function (e) { if (e.date) days[e.date] = 1; });
      var n = Object.keys(days).length;
      head.appendChild(el('span', 'le-groupcount', n === 1 ? '1 date' : n + ' dates'));
      wrap.appendChild(head);

      var tiles = el('div', 'le-tiles');
      g.events.forEach(function (e) { tiles.appendChild(self.tile(e)); });
      wrap.appendChild(tiles);

      self.el.groups.appendChild(wrap);
    });
  };

  Events.prototype.tile = function (e) {
    var d = parseDate(e.date);
    var full = e.address + ', ' + e.city + ', ' + e.state + ' ' + e.zip;

    var tile = el('div', 'le-tile');

    var top = el('div', 'le-tiletop');
    top.appendChild(el('div', 'le-date',
      DOWS[d.getDay()] + ' ' + MONTHS[d.getMonth()].slice(0, 3) + ' ' + d.getDate()));
    var kind = el('div', 'le-kind', e.kind || 'Tasting');
    kind.dataset.kind = e.kind || 'Tasting';
    top.appendChild(kind);
    tile.appendChild(top);

    var body = el('div');
    body.appendChild(el('div', 'le-venue', e.venue));
    body.appendChild(el('div', 'le-where',
      e.city + ', ' + e.state + (e.time ? ' \u00b7 ' + e.time : '')));
    tile.appendChild(body);

    var actions = el('div', 'le-actions');

    // The href stays a working Google Maps link so the button still does
    // something if luba-directions.js fails to load; when it is present it
    // intercepts the click and offers Apple / Google / copy instead.
    var dir = el('a', 'le-btn-primary', 'Directions');
    dir.href = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(full);
    dir.target = '_blank';
    dir.rel = 'noopener';
    if (window.LubaDirections) window.LubaDirections.attach(dir, full);
    actions.appendChild(dir);

    // A real .ics file, not a Google Calendar link. The old link sent everyone
    // to a Google web page that needs a Google account, and it carried
    // `dates=YYYYMMDD/YYYYMMDD` \u2014 an ALL-DAY event with start == end, so the
    // actual 4pm-7pm was thrown away even for Google users.
    //
    // .ics is the format every calendar consumes natively: iOS Calendar,
    // Android, Outlook, Apple Calendar and Google all open it.
    var cal = el('a', 'le-btn-quiet', 'Add to calendar');
    attachIcs(cal, e, full);
    actions.appendChild(cal);

    tile.appendChild(actions);
    return tile;
  };

  Events.prototype.emptyState = function () {
    var wrap = el('div', 'le-empty');
    wrap.appendChild(el('div', 'le-empty-h', 'No Tastings There Yet'));
    wrap.appendChild(el('p', 'le-empty-p',
      'We add dates every week. Tell us where you would like us to pour and we will see what we can do.'));
    if (this.cfg.emptyCta) {
      var a = el('a', 'le-empty-cta', this.cfg.emptyCta);
      a.href = this.cfg.emptyUrl || '/pages/contact';
      wrap.appendChild(a);
    }
    return wrap;
  };

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-luba-events]'),
      function (root) {
        if (root.dataset.eventsReady) return;
        root.dataset.eventsReady = '1';
        new Events(root);
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
